import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { trackEvent } from "@hopper-b2b/api";
import { I18nMarkup, useI18nContext } from "@hopper-b2b/i18n";
import {
  type DisruptionOffer,
  DisruptionTrackingEvents,
  FintechCoverageType,
  type FintechProductOption,
  FintechProductType,
} from "@hopper-b2b/types";
import { DetailHalfSheet, FintechPurchase, Slot } from "@hopper-b2b/ui";
import {
  type HeaderPayload,
  getEnvVariables,
  useDeviceTypes,
  useEnableFintechPurchaseErrorState,
  useUberBridge,
} from "@hopper-b2b/utilities";
import { ClientContext } from "../../../../App";
import type { IStoreState } from "../../../../reducers/types";
import { FintechProduct } from "../../../shop/components/FintechCustomizePage/FintechProduct";
import { useGoToNextStep } from "../../../shop/hooks";
import {
  selectedTripSelector,
  tripDetailsSelector,
} from "../../../shop/reducer";
import { setSelectedScheduleChangeOffer } from "../../actions/actions";
import {
  getScheduleChangeOffer,
  getSelectedScheduleChangeOffer,
} from "../../reducer";
import type { DisruptionContentProps } from "../DisruptionOffer";

const OPEN_DISRUPTION_DETAILS = "open_disruption_details";

export const ScheduleChangeOffer = ({
  goToNextStep,
  hideContinueButton,
}: DisruptionContentProps) => {
  const { setHeader } = useUberBridge();
  const { t, formatCurrency } = useI18nContext();
  const enableFintechPurchaseErrorState = useEnableFintechPurchaseErrorState();

  const clientContext = useContext(ClientContext);

  const { matchesMobile } = useDeviceTypes();

  const isHopper = getEnvVariables("clientName") === "hopper";

  const dispatch = useDispatch();

  const scheduleChangeOffer = useSelector(getScheduleChangeOffer);
  const selectedOffer = useSelector(getSelectedScheduleChangeOffer);

  const [learnMoreOpen, setLearnMoreOpen] = useState<boolean>(false);

  const noCoverageSelected = selectedOffer === null;

  const scheduleChangeOfferQuote = scheduleChangeOffer?.quotes?.[0];
  const scheduleChangeOfferCost = useMemo(
    () =>
      formatCurrency(scheduleChangeOfferQuote?.pricePerPax?.fiat?.value, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }),
    [formatCurrency, scheduleChangeOfferQuote]
  );

  // Clear selection on load
  useEffect(() => {
    if (enableFintechPurchaseErrorState) {
      dispatch(setSelectedScheduleChangeOffer(selectedOffer));
    }
  }, [dispatch, selectedOffer]);

  useEffect(() => {
    trackEvent({
      eventName: DisruptionTrackingEvents.VIEWED_DELAY_GUARANTEE,
      properties: undefined,
    });
  }, []);

  useEffect(() => {
    if (learnMoreOpen) {
      trackEvent({
        eventName: DisruptionTrackingEvents.TAP_DELAYS_PROTECTION_LEARN_MORE,
        properties: undefined,
      });
    }
  }, [learnMoreOpen]);

  const handleSelectLearnMore = useCallback(() => {
    setLearnMoreOpen(!learnMoreOpen);
  }, [learnMoreOpen]);

  useEffect(() => {
    setHeader({
      customButtons: [
        {
          icon: "CIRCLEI",
          action: {
            name: OPEN_DISRUPTION_DETAILS,
            callback: handleSelectLearnMore,
          },
        },
      ],
    } as HeaderPayload);
  }, [handleSelectLearnMore, setHeader, t]);

  const handleOfferSelection = useCallback(
    (offer: DisruptionOffer) => {
      dispatch(setSelectedScheduleChangeOffer(offer));

      if (offer) {
        trackEvent({
          eventName: DisruptionTrackingEvents.ADDED_DELAY_GUARANTEE,
          properties: scheduleChangeOfferQuote?.trackingProperties,
        });
      } else {
        trackEvent({
          eventName: DisruptionTrackingEvents.DECLINED_DELAY_GUARANTEE,
          properties: scheduleChangeOfferQuote?.trackingProperties,
        });
      }
    },
    [dispatch, scheduleChangeOfferQuote?.trackingProperties]
  );

  useEffect(() => {
    if (scheduleChangeOffer && !enableFintechPurchaseErrorState) {
      handleOfferSelection(scheduleChangeOffer);
      dispatch(setSelectedScheduleChangeOffer(scheduleChangeOffer));
    }
  }, [dispatch, handleOfferSelection, scheduleChangeOffer]);

  const options: FintechProductOption[] = useMemo(() => {
    return [
      {
        value: FintechCoverageType.Full,
        title: t("disruptionPurchase.fullCoverage"),
        cost: scheduleChangeOfferCost,
        description: [t("disruptionPurchase.fullCoverageDescription")],
        selected: selectedOffer === scheduleChangeOffer,
        onClick: () => handleOfferSelection(scheduleChangeOffer),
        ctaLabel: t("acceptCta"),
      },
      {
        value: FintechCoverageType.None,
        title: t("disruptionPurchase.noCoverage"),
        cost: formatCurrency(0, {
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        }),
        description: [t("disruptionPurchase.description")],

        selected: noCoverageSelected,
        onClick: () => handleOfferSelection(null),
        ctaLabel: t("declineCta"),
      },
    ];
  }, [
    formatCurrency,
    handleOfferSelection,
    noCoverageSelected,
    scheduleChangeOffer,
    scheduleChangeOfferCost,
    selectedOffer,
    t,
  ]);

  const hopperOptions: FintechProductOption[] = useMemo(() => {
    return [
      {
        value: FintechCoverageType.Full,
        title: t("flightDelayGuarantee.fullCoverageTitle"),
        cost: scheduleChangeOfferCost,
        selected: selectedOffer === scheduleChangeOffer,
        onClick: () => handleOfferSelection(scheduleChangeOffer),
        recommended: t("flightDelayGuarantee.recommended"),
      },
      {
        value: FintechCoverageType.Partial,
        title: t("flightDelayGuarantee.partialCoverageTitle"),
        cost: scheduleChangeOfferCost,
        selected: selectedOffer === scheduleChangeOffer,
        onClick: () => handleOfferSelection(scheduleChangeOffer),
      },
      {
        value: FintechCoverageType.None,
        title: t("flightDelayGuarantee.noCoverageTitle"),
        selected: noCoverageSelected,
        description: t("flightDelayGuarantee.noCoverageDescription"),
        onClick: () => handleOfferSelection(null),
      },
    ];
  }, [
    handleOfferSelection,
    noCoverageSelected,
    scheduleChangeOffer,
    scheduleChangeOfferCost,
    selectedOffer,
    t,
  ]);

  const selectedTrip = useSelector(selectedTripSelector);
  const tripId = selectedTrip.tripId || "";
  const tripDetails = useSelector((state: IStoreState) =>
    tripDetailsSelector(state, tripId)
  );

  const airlineCodes = useMemo(() => {
    if (tripDetails?.slices) {
      return tripDetails?.slices.flatMap((slice) => {
        return slice?.segmentDetails.map((segment) => {
          return segment?.airlineCode;
        });
      });
    }
    return [];
  }, [tripDetails]);

  const jetBlue = useMemo(
    () => airlineCodes.every((airline) => airline === "B6"),
    [airlineCodes]
  );

  const headerSubtitle = useMemo(
    () =>
      jetBlue
        ? t("disruptionPurchase.airlineAgreementSubtitle", {
            airline: "Jetblue",
          })
        : t("disruptionPurchase.subtitle"),
    [jetBlue, t]
  );

  const onNext = useGoToNextStep();

  if (isHopper) {
    return (
      <FintechProduct
        options={hopperOptions}
        header={t?.(
          matchesMobile
            ? "flightDelayGuarantee.mobileHeader"
            : "flightDelayGuarantee.header"
        )}
        infoTextKey="flightDelayGuarantee.headerSubtext"
        bodyText={t?.("flightDelayGuarantee.description")}
        type={FintechProductType.Disruption}
        hideContinueButton={hideContinueButton}
        goToNextStep={onNext}
        disableContinueButton={selectedOffer === undefined}
      />
    );
  }

  return (
    <div className="disruption-purchase">
      <Slot
        id="flights-fintech-purchase"
        product={FintechProductType.Disruption}
        className="disruption"
        options={options}
        headerTitle={t("disruptionPurchase.header")}
        headerSubtitle={headerSubtitle}
        headerImgSrc={
          clientContext?.assets && clientContext?.assets["airplaneFintech"]
        }
        clientAssets={clientContext.assets}
        ctaLabel={
          selectedOffer && !noCoverageSelected
            ? t("acceptCta")
            : t("declineCta")
        }
        handleContinue={hideContinueButton ? undefined : goToNextStep}
        disabled={selectedOffer === undefined}
        component={
          <FintechPurchase
            className="disruption"
            options={options}
            headerTitle={t("disruptionPurchase.header")}
            headerSubtitle={headerSubtitle}
            headerImgSrc={
              clientContext?.assets && clientContext?.assets["airplaneFintech"]
            }
            clientAssets={clientContext.assets}
            ctaLabel={
              selectedOffer && !noCoverageSelected
                ? t("acceptCta")
                : t("declineCta")
            }
            handleContinue={hideContinueButton ? undefined : goToNextStep}
            disabled={selectedOffer === undefined}
          />
        }
      />
      <Slot
        id="fintech-learn-more-modal"
        productType={FintechProductType.Disruption}
        open={learnMoreOpen}
        onClose={handleSelectLearnMore}
        component={
          <DetailHalfSheet
            productType={FintechProductType.Disruption}
            open={learnMoreOpen}
            onClose={handleSelectLearnMore}
            header={t("disruptionInfoModal.header")}
            title={t("disruptionInfoModal.title")}
            description={
              <I18nMarkup tKey={"disruptionInfoModal.description"} />
            }
            descriptionAdditional
          />
        }
      />
    </div>
  );
};
